import { getSizeDimensionsFromString, getSizeString } from '@lib/size'
import { type FrameBuilderStateAction } from './state-reducer'
import { type FrameBuilderConfiguration, type FrameBuilderState } from './types'
import { isNumeric } from '@lib/helpers'

type FieldType =
  | 'artwork_size'
  | 'background'
  | 'items'
  | 'mount_height'
  | 'mount_width'

type ItemType =
  | 'profile'
  | 'texture'
  | 'passepartout_color'
  | 'glass'
  | 'spacer_size'
  | 'spacer_color'

interface FrameBuilderDocument {
  id: string
  type: ItemType
}

type FrameBuilderDocumentIdMap = Record<string, FrameBuilderDocument>

/**
 * Generates a string from frame builder state.
 */
export const getFrameString = (state: FrameBuilderState) => {
  // Artwork size
  const artworkSizeString =
    state.artwork.width && state.artwork.height
      ? getSizeString(state.artwork.width, state.artwork.height, {
          decimalSeparator: '.',
        })
      : ''

  // Background color
  const backgroundColorString = state.artwork?.backgroundColor?.current ?? ''

  // Selected item IDs
  const itemIds = [
    state.profile?._id,
    state.texture?._id,
    state.passepartout.color?._id,
    state.glass?._id,
    state.spacerSize?._id,
    state.spacerColor?._id,
  ].filter(Boolean) as string[]
  const itemIdsString = itemIds.join(',')

  // Mount margin width and height
  const mountWidthString = state.passepartout.width
    ? `${state.passepartout.width}`
    : ''
  const mountHeightString = state.passepartout.height
    ? `${state.passepartout.height}`
    : ''

  const frameStringParts: [FieldType, string][] = [
    ['artwork_size', artworkSizeString],
    ['background', backgroundColorString],
    ['items', itemIdsString],
    ['mount_height', mountHeightString],
    ['mount_width', mountWidthString],
  ]
  return frameStringParts
    .map((frameStringPart) => frameStringPart.join(':'))
    .join(';')
}

/**
 * Gets frame builder documents mapped by IDs.
 */
function getFrameBuilderDocumentMap(
  frameBuilderConfiguration: FrameBuilderConfiguration,
) {
  const documentIdMap: FrameBuilderDocumentIdMap = {}

  frameBuilderConfiguration.profiles.forEach((profile) => {
    documentIdMap[profile._id] = {
      id: profile._id,
      type: 'profile',
    }
  })

  frameBuilderConfiguration.textures.forEach((texture) => {
    documentIdMap[texture._id] = {
      id: texture._id,
      type: 'texture',
    }
  })

  frameBuilderConfiguration.passepartoutColors.forEach((passepartoutColor) => {
    documentIdMap[passepartoutColor._id] = {
      id: passepartoutColor._id,
      type: 'passepartout_color',
    }
  })

  frameBuilderConfiguration.glass.forEach((glass) => {
    documentIdMap[glass._id] = {
      id: glass._id,
      type: 'glass',
    }
  })

  frameBuilderConfiguration.spacerSizes.forEach((spacerSize) => {
    documentIdMap[spacerSize._id] = {
      id: spacerSize._id,
      type: 'spacer_size',
    }
  })

  frameBuilderConfiguration.spacerColors.forEach((spacerColor) => {
    documentIdMap[spacerColor._id] = {
      id: spacerColor._id,
      type: 'spacer_color',
    }
  })

  return documentIdMap
}

/**
 * Gets reducer action that adds frame builder configuration document to state.
 */
const getFrameBuilderDocumentStateAction = (
  frameBuilderConfiguration: FrameBuilderConfiguration,
  state: FrameBuilderState,
  type: ItemType,
  id: string,
): FrameBuilderStateAction | undefined => {
  switch (type) {
    case 'profile': {
      const profile = frameBuilderConfiguration.profiles.find(
        (profile) => profile._id === id,
      )

      if (
        typeof profile === 'undefined' ||
        state.profile?._id === profile._id
      ) {
        return
      }

      return {
        type: 'frameStyle',
        profile,
      }
    }

    case 'texture': {
      const texture = frameBuilderConfiguration.textures.find(
        (texture) => texture._id === id,
      )

      if (
        typeof texture === 'undefined' ||
        state.texture?._id === texture._id
      ) {
        return
      }

      return {
        type: 'frameStyle',
        texture,
      }
    }

    case 'passepartout_color': {
      const passepartoutColor =
        frameBuilderConfiguration.passepartoutColors.find(
          (passepartoutColor) => passepartoutColor._id === id,
        )

      if (
        typeof passepartoutColor === 'undefined' ||
        state.passepartout.color?._id === passepartoutColor._id
      ) {
        return
      }

      return {
        type: 'passepartout',
        color: passepartoutColor,
      }
    }

    case 'glass': {
      const glass = frameBuilderConfiguration.glass.find(
        (glass) => glass._id === id,
      )

      if (typeof glass === 'undefined' || state.glass?._id === glass._id) {
        return
      }

      return {
        type: 'glass',
        glass,
      }
    }

    case 'spacer_size': {
      const spacerSize = frameBuilderConfiguration.spacerSizes.find(
        (spacerSize) => spacerSize._id === id,
      )

      if (
        typeof spacerSize === 'undefined' ||
        state.spacerSize?._id === spacerSize._id
      ) {
        return
      }

      return {
        type: 'spacer',
        size: spacerSize,
      }
    }

    case 'spacer_color': {
      const spacerColor = frameBuilderConfiguration.spacerColors.find(
        (spacerColor) => spacerColor._id === id,
      )

      if (
        typeof spacerColor === 'undefined' ||
        state.spacerColor?._id === spacerColor._id
      ) {
        return
      }

      return {
        type: 'spacer',
        color: spacerColor,
      }
    }
  }
}

/**
 * Gets reducer action that adds frame builder configuration documents to state.
 */
const getFrameBuilderStringStateAction = (
  frameBuilderConfiguration: FrameBuilderConfiguration,
  state: FrameBuilderState,
  value: string,
) => {
  const documentMap = getFrameBuilderDocumentMap(frameBuilderConfiguration)
  const document = documentMap[value]

  if (!document) {
    return
  }

  const documentStateAction = getFrameBuilderDocumentStateAction(
    frameBuilderConfiguration,
    state,
    document.type,
    document.id,
  )

  return documentStateAction
}

/**
 * Gets reducer actions that add frame builder configuration documents and background color to state.
 */
const getFrameBuilderStringStateActions = (
  frameBuilderConfiguration: FrameBuilderConfiguration,
  defaultState: FrameBuilderState,
  field: string,
) => {
  const actions: FrameBuilderStateAction[] = []
  const fieldParts = field.split(':', 2)
  const fieldType = fieldParts[0] as FieldType | undefined
  const fieldValue = fieldParts[1] as string | undefined

  if (typeof fieldType === 'undefined' || typeof fieldValue === 'undefined') {
    return actions
  }

  switch (fieldType) {
    case 'artwork_size': {
      // Check if value has 2 sizes separated by "x"
      if (fieldValue.includes('x') && /^[0-9,.]+x[0-9,.]+$/g.test(fieldValue)) {
        const [artworkWidth, artworkHeight] =
          getSizeDimensionsFromString(fieldValue)
        actions.push({
          type: 'artworkSize',
          width: artworkWidth,
          height: artworkHeight,
        })
      }

      break
    }

    case 'background': {
      actions.push({
        type: 'artworkImage',
        color: fieldValue,
      })

      break
    }

    case 'items': {
      fieldValue.split(',').forEach((valuePart) => {
        const action = getFrameBuilderStringStateAction(
          frameBuilderConfiguration,
          defaultState,
          valuePart,
        )

        if (action) {
          actions.push(action)
        }
      })

      break
    }

    case 'mount_height': {
      if (isNumeric(fieldValue)) {
        actions.push({
          type: 'passepartout',
          height: Number(fieldValue),
        })
      }

      break
    }

    case 'mount_width': {
      if (isNumeric(fieldValue)) {
        actions.push({
          type: 'passepartout',
          width: Number(fieldValue),
        })
      }

      break
    }
  }

  return actions
}

/**
 * Parses state string into frame builder state actions.
 */
export const getStateActionsFromFrameString = (
  frameBuilderConfiguration: FrameBuilderConfiguration,
  defaultState: FrameBuilderState,
  stateString: string,
) => {
  const stateStringParts = stateString.split(';')

  return stateStringParts.flatMap((stateStringPart) => {
    return getFrameBuilderStringStateActions(
      frameBuilderConfiguration,
      defaultState,
      stateStringPart,
    )
  })
}
